import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { environment } from 'src/environment/environment';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { DataImportsService } from '../_services/data-imports.service';
import { DataImportRowsService } from '../_services/data-import-rows.service';
import { DataImport } from '../_types/data-import';
import { DataImportRow } from '../_types/data-import-row';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { Table } from 'primeng/table';

/** Una causa de error o de omisión, con las veces que aparece en la importación. */
interface ErrorCause {
    message: string;
    count: number;
}

/** Cada cuánto se refresca sola la ficha de una importación sin terminar. */
const POLL_MS = 30000;

/** Estados en los que la importación todavía puede cambiar por su cuenta. */
const RUNNING_STATUSES = ['Pendiente', 'EnCurso'];

interface ErrorSummary {
    /** Agrupadas por tipo, sin el valor concreto: "Tramitador no encontrado". */
    errorTypes: ErrorCause[];
    /** Con el valor que falló: "Tramitador 'pepe' no encontrado". */
    errors: ErrorCause[];
    skippedTypes: ErrorCause[];
    skipped: ErrorCause[];
    errorRows: number;
    skippedRows: number;
}

type TagSeverity = 'secondary' | 'info' | 'success' | 'danger' | 'warn' | 'contrast';

@Component({
    selector: 'app-imports-detail',
    templateUrl: './imports-detail.component.html',
    styleUrls: ['./imports-detail.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class ImportsDetailComponent implements OnInit, OnDestroy {
    @ViewChild('rowsGrid') rowsGrid?: Table;

    dataImport?: DataImport;
    rows: DataImportRow[] = [];
    rowsTotal = 0;
    loadingRows = false;
    onlyErrors = false;
    summary?: ErrorSummary;
    loadingSummary = false;

    showRerun = false;
    rerunning = false;
    /** Copia editable de las opciones, para no tocar la importación hasta confirmar. */
    rerunOptions = { mode: 'Crear', stopOnFirstError: false, dryRun: true, allowDuplicates: false, truncateValues: false };

    modes = [
        { label: 'Crear', value: 'Crear' },
        { label: 'Crear y actualizar', value: 'CrearActualizar' }
    ];

    pdfOptions: MenuItem[] = [
        {
            label: 'Resumido',
            icon: 'pi pi-list',
            command: () => this.exportPdf(false)
        },
        {
            label: 'Detallado',
            icon: 'pi pi-align-left',
            command: () => this.exportPdf(true)
        }
    ];
    promotedImportId: string | null = null;

    /** Ultimo evento del paginador, para poder recargar la pagina que se esta viendo. */
    /**
     * Instante en que se leyó la importación. La velocidad y la estimación se calculan
     * contra esta marca y no contra el reloj: si no, entre dos refrescos el tiempo
     * seguiría corriendo con las filas congeladas y la velocidad bajaría sola.
     */
    private readAt = new Date();

    private lastRowsEvent: any = { first: 0, rows: 50 };
    private importId!: string;
    private pollTimer: any;

    typeLabels: Record<string, string> = {
        Expedientes: 'Expedientes', Incidencias: 'Incidencias', Notas: 'Notas',
        Operarios: 'Operarios', Trabajos: 'Trabajos', Visitas: 'Visitas'
    };
    statusLabels: Record<string, string> = {
        Pendiente: 'Pendiente', EnCurso: 'En curso', Completada: 'Completada', Error: 'Error'
    };
    rowStatusLabels: Record<string, string> = {
        Imported: 'Importada', Skipped: 'Omitida', Error: 'Error', Validated: 'Validada'
    };
    modeLabels: Record<string, string> = {
        Crear: 'Crear', CrearActualizar: 'Crear y actualizar'
    };

    statusSeverities: Record<string, TagSeverity> = {
        Pendiente: 'secondary', EnCurso: 'info', Completada: 'success', Error: 'danger'
    };
    rowStatusSeverities: Record<string, TagSeverity> = {
        Imported: 'success', Skipped: 'warn', Error: 'danger', Validated: 'success'
    };

    get progress(): number {
        const di = this.dataImport;
        if (!di) return 0;
        if (di.progressPercent) return Math.round(di.progressPercent);
        if (di.totalRows) return Math.round((di.processedRows / di.totalRows) * 100);
        return 0;
    }

    /** Segundos entre el arranque y el final, o entre el arranque y la última lectura. */
    private get elapsedSeconds(): number | null {
        const start = this.toDate(this.dataImport?.startedAt);
        if (!start) return null;

        const end = this.toDate(this.dataImport?.finishedAt) ?? this.readAt;
        const seconds = (end.getTime() - start.getTime()) / 1000;
        return seconds > 0 ? seconds : null;
    }

    /** Velocidad media desde el arranque, en filas por segundo. */
    get rowsPerSecond(): number | null {
        const processed = this.dataImport?.processedRows ?? 0;
        const elapsed = this.elapsedSeconds;
        if (!processed || elapsed === null) return null;
        return processed / elapsed;
    }

    /** Segundos que faltan al ritmo medio. Solo mientras la importación siga viva. */
    get remainingSeconds(): number | null {
        const di = this.dataImport;
        if (!di || !this.isRunning) return null;

        const pending = (di.totalRows ?? 0) - (di.processedRows ?? 0);
        const speed = this.rowsPerSecond;
        if (pending <= 0 || !speed) return null;

        return pending / speed;
    }

    get speedLabel(): string | null {
        const speed = this.rowsPerSecond;
        if (speed === null) return null;

        // Por debajo de 10 el entero se queda corto: 0 registros/s no dice nada.
        const value = speed >= 10 ? Math.round(speed) : Math.round(speed * 10) / 10;
        return `${value.toLocaleString('es-ES')} registros/s`;
    }

    get remainingLabel(): string | null {
        const seconds = this.remainingSeconds;
        return seconds === null ? null : `quedan ~${this.formatDuration(seconds)}`;
    }

    private formatDuration(seconds: number): string {
        const total = Math.round(seconds);
        if (total < 60) return `${total} s`;

        const minutes = Math.floor(total / 60);
        if (minutes < 60) {
            const rest = total % 60;
            return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
        }

        const hours = Math.floor(minutes / 60);
        const restMinutes = minutes % 60;
        return restMinutes ? `${hours} h ${restMinutes} min` : `${hours} h`;
    }

    /** Las fechas llegan como Date o como texto según pase o no por el interceptor. */
    private toDate(value: any): Date | null {
        if (!value) return null;
        const date = value instanceof Date ? value : new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }

    get successRate(): number {
        const di = this.dataImport;
        if (!di?.processedRows) return 0;
        return Math.round((di.importedRows / di.processedRows) * 100);
    }

    get canPromote(): boolean {
        const di = this.dataImport;
        if (!di?.dryRun || this.promotedImportId) return false;
        return di.status === 'Completada' || di.status === 'Error';
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private importsService: DataImportsService,
        private rowsService: DataImportRowsService,
        private confirmation: ConfirmationService,
        private topbarService: TopbarService
    ) {}

    ngOnInit(): void {
        this.topbarService.setTitle('Detalle de importación');
        this.topbarService.addBreadcrumb('Importaciones', '/imports');
        this.importId = this.route.snapshot.paramMap.get('id')!;
        // Las filas las pide la propia tabla al arrancar, via onLazyLoad.
        this.loadImport();
        this.loadSummary();
        this.pollTimer = setInterval(() => this.autoRefresh(), POLL_MS);
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    /** La importación aún puede cambiar sola, así que merece la pena vigilarla. */
    get isRunning(): boolean {
        return RUNNING_STATUSES.includes(this.dataImport?.status ?? '');
    }

    /**
     * Refresco automatico mientras la importacion sigue viva. En cuanto termina
     * deja de pedir: una importacion acabada ya no cambia.
     */
    private autoRefresh(): void {
        if (!this.isRunning) { this.stopPolling(); return; }
        this.refresh(true);
    }

    private stopPolling(): void {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    /** Recuento de causas de error y de omision, agregado por la API. */
    private loadSummary(silent = false): void {
        if (!silent) this.loadingSummary = true;
        const url = `${environment.api_gw}common/dataImports/${this.importId}/errorSummary`;
        if (silent) this.rowsService.loaderService.skipRequest(url);
        (this.rowsService as any).http.get(url).subscribe({
            next: (res: ErrorSummary) => {
                this.summary = this.withEmptyLists(res);
                this.loadingSummary = false;
            },
            error: () => { this.loadingSummary = false; }
        });
    }

    private loadImport(silent = false): void {
        if (silent) this.importsService.loaderService.skipRequest(`${this.importsService.getAPIUrl()}/${this.importId}`);
        this.importsService.get(this.importId).subscribe(res => {
            this.dataImport = res;
            this.readAt = new Date();
            if (res?.dryRun) this.checkPromoted(this.importId);
        });
    }

    /**
     * Garantiza que las cuatro listas existan. Una API anterior a los recuentos por tipo
     * no devuelve errorTypes ni skippedTypes, y la plantilla reventaba al pedirles el
     * length. Con listas vacías la pestaña se limita a no pintar esos bloques.
     */
    private withEmptyLists(res: ErrorSummary): ErrorSummary {
        return {
            ...res,
            errorTypes: res?.errorTypes ?? [],
            errors: res?.errors ?? [],
            skippedTypes: res?.skippedTypes ?? [],
            skipped: res?.skipped ?? [],
            errorRows: res?.errorRows ?? 0,
            skippedRows: res?.skippedRows ?? 0
        };
    }

    /** Vuelve a pedir la importacion y la pagina de filas que se esta viendo. */
    refresh(silent = false): void {
        this.loadImport(silent);
        this.loadRows(this.lastRowsEvent, silent);
        this.loadSummary(silent);
    }

    private checkPromoted(id: string): void {
        const filters = {
            sourceDataImportId: new Filter(FilterOperation.equals, 'sourceDataImportId', '', id)
        };
        const params: IParamsCollection = {
            page: { size: 1 },
            filter: FilterBuilder.fromObject(filters).build()
        };
        this.importsService.all(params, true).subscribe({
            next: (res: any) => this.promotedImportId = res?.data?.[0]?.id ?? null,
            error: () => this.promotedImportId = null
        });
    }

    loadRows($event: any, silent = false): void {
        this.lastRowsEvent = $event;

        const filters: any = {
            dataImportId: new Filter(FilterOperation.equals, 'dataImportId', '', this.importId)
        };
        if (this.onlyErrors) filters.status = new Filter(FilterOperation.equals, 'status', '', 'Error');

        const params: IParamsCollection = {
            sort: ['rowNumber'],
            page: { number: $event.first + 1, size: $event.rows },
            filter: FilterBuilder.fromObject(filters).build()
        };

        if (!silent) this.loadingRows = true;
        this.rowsService.all(params, silent).subscribe({
            next: (res: any) => {
                this.rows = res?.data ?? [];
                this.rowsTotal = res?.meta?.['total'] ?? 0;
                this.loadingRows = false;
            },
            error: () => { this.loadingRows = false; }
        });
    }

    toggleErrors(): void {
        this.onlyErrors = !this.onlyErrors;
        // Cambia el conjunto de filas: volvemos a la primera pagina.
        if (this.rowsGrid) this.rowsGrid.first = 0;
        this.loadRows({ ...this.lastRowsEvent, first: 0 });
    }

    promote(): void {
        const di = this.dataImport;
        if (!di?.id) return;

        const message = di.errorRows > 0
            ? `La prueba encontró ${di.errorRows} fila(s) con error, que no se importarán. ¿Lanzar la importación con el resto?`
            : '¿Lanzar la importación real con este mismo fichero?';

        this.confirmation.confirm({
            header: 'Importar de verdad',
            message,
            acceptLabel: 'Sí, importar',
            rejectLabel: 'Cancelar',
            accept: () => this.importsService.promote(di.id!).subscribe({
                next: (res: any) => this.router.navigate(['/imports', res.id]),
                error: (err: any) => alert('No se pudo lanzar la importación: ' + (err?.error?.errors?.[0]?.message ?? err?.message ?? 'sin detalle'))
            })
        });
    }

    /** Abre el diálogo con las opciones actuales de la importación. */
    openRerun(): void {
        const di = this.dataImport;
        if (!di) return;

        this.rerunOptions = {
            mode: di.mode,
            stopOnFirstError: di.stopOnFirstError,
            dryRun: di.dryRun,
            allowDuplicates: di.allowDuplicates,
            truncateValues: di.truncateValues
        };
        this.showRerun = true;
    }

    /** La importación no se puede relanzar mientras se está ejecutando. */
    get canRerun(): boolean {
        return this.dataImport?.status !== 'EnCurso';
    }

    rerun(): void {
        if (!this.dataImport?.id) return;
        this.rerunning = true;

        const url = `${environment.api_gw}common/dataImports/${this.dataImport.id}/rerun`;
        (this.importsService as any).http.post(url, this.rerunOptions).subscribe({
            next: () => {
                this.rerunning = false;
                this.showRerun = false;
                this.refresh();
                // La tabla vuelve a la primera página: las filas anteriores ya no existen.
                if (this.rowsGrid) this.rowsGrid.first = 0;
                this.loadRows({ ...this.lastRowsEvent, first: 0 });
            },
            error: () => { this.rerunning = false; }
        });
    }

    openPromoted(): void {
        if (this.promotedImportId) this.router.navigate(['/imports', this.promotedImportId]);
    }

    /** @param detailed con la tabla de filas con error, o solo el resumen por causa. */
    exportPdf(detailed: boolean): void {
        if (!this.dataImport?.id) return;
        const url = `${environment.api_gw}common/dataImports/${this.dataImport.id}/pdf?detailed=${detailed}`;
        window.open(url, '_blank');
    }
}
